<template>
  <div
    class="sb-filters-panel-checkbox"
    data-sb-automation="sb-filters-panel-checkbox-container"
  >
    <div v-if="searchEnabled">
      <SbTextInput
        v-model:value="searchTerm"
        :placeholder="$t('common.search')"
        class="sb-filter-search"
      >
        <template #prepend>
          <i
            aria-hidden="true"
            class="sb-filter-search__icon sb-icon-search"
            @click="handleSearch"
          />
        </template>
      </SbTextInput>
    </div>
    <template
      v-for="(option) in displayedOptions"
      :key="option.key"
    >
      <SbCheckbox
        v-if="option.isReset"
        :version="dsComponentDetails.version"
        :value="true"
        :checked="internalValue.length ? false : true"
        v-bind="filter.props"
        data-sb-automation="sb-filters-panel-checkbox-input"
        @update:checked="handleReset"
      >
        <sb-text :value="option.name" />
      </SbCheckbox>
      <SbCheckbox
        v-else
        v-model:checked="internalValue"
        :version="dsComponentDetails.version"
        data-sb-automation="sb-filters-panel-checkbox-input"
        :value="option.key"
        :checked="internalValue"
        :name="option.name"
        v-bind="filter.props"
      >
        <sb-text :value="option.name" />
      </SbCheckbox>
    </template>
    <div v-if="hasDisplayQty && showMoreText">
      <SbButton
        :version="dsComponentDetails.version"
        class="filter-show-more"
        variation="link"
        size="small"
        @click="toggleExpand"
      >
        <sb-text :value="showMoreText" />
      </SbButton>
    </div>
  </div>
</template>
<script>
import SbButton from '@src/components/Button/SbButton.vue';
import SbTextInput from '@src/components/Input/SbTextInput.vue';
import SbCheckbox from '@src/components/Checkbox/SbCheckbox.vue';
import SbText from '@src/components/Text/SbText.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';

export default {
  name: 'SbCollapsableCheckbox',
  components: {
    SbButton,
    SbCheckbox,
    SbTextInput,
    SbText,
  },
  mixins: [versionedComponentsMixin],
  props: {
    filter: {
      type: Object,
      required: true,
    },
    filterValue: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['update:filter-value'],
  data: () => ({
    searchTerm: '',
    expanded: false,
  }),
  computed: {
    internalValue: {
      get() {
        // note - default value must be array so multiple items can be checked
        const v = this.filterValue?.value ?? [];
        return Array.isArray(v) ? v : [v];
      },
      set(value) {
        this.$emit('update:filter-value', {
          value,
        });
        if (value.length === 0) {
          // collapse on reset
          this.expanded = false;
        } else if (!this.expanded && this.showMoreQty > 0) {
          this.expanded = true;
        }
      },
    },
    optionsList() {
      const options = [...this.filter.options];
      if (this.expanded || !this.hasDisplayQty) return options;

      // if 'Show More' functionality active -- sort selected items first
      const selectedKeys = this.internalValue.reduce((p, c) => {
        // eslint-disable-next-line no-param-reassign
        p[c] = true;
        return p;
      }, {});
      options.sort((a, b) => {
        if (b.isReset) return 1; // Reset button is always the first
        const activeA = selectedKeys[a.key] ?? false;
        const activeB = selectedKeys[b.key] ?? false;
        if (activeA === activeB) return 0;
        return activeB ? 1 : -1;
      });
      return options;
    },
    searchEnabled() {
      return this.filter.searchEnabled ?? false;
    },
    displayedOptions() {
      let options = [...this.optionsList];
      if (this.searchEnabled && this.searchTerm !== '') {
        const s = this.searchTerm.toLowerCase();
        options = options.filter((o) => o.name.toLowerCase().includes(s));
      }
      if (this.displayQty && !this.expanded) {
        options = options.slice(0, this.displayQty);
      }
      return options;
    },
    hasDisplayQty() {
      return this.filter.displayQty != null;
    },
    displayQty() {
      if (this.filter.displayQty == null) return 0;
      return Math.max(this.filter.displayQty, this.internalValue.length + 2);
    },
    showMoreQty() {
      if (this.displayQty === 0 || !this.optionsList) return 0;
      return (this.optionsList.length - this.displayedOptions.length);
    },
    showMoreText() {
      if (!this.expanded && !this.showMoreQty) return '';
      return this.expanded ? this.$t('sbFilter.showLess') : this.$t('sbFilter.showMore', { showMoreQty: this.showMoreQty });
    },
  },
  methods: {
    handleReset() {
      this.internalValue = [];
    },
    toggleExpand() {
      this.expanded = !this.expanded;
    },
  },
};
</script>
